import { useEffect } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { Briefcase, Bell, ListTodo, CalendarDays, LayoutGrid, LogOut } from "lucide-react";
import { useCompanionAuth } from "@/context/CompanionAuthContext";
import { Toaster } from "@/components/ui/sonner";

const NAV = [
  { to: "/companion",          label: "Today",    icon: LayoutGrid,    end: true },
  { to: "/companion/jobs",     label: "Jobs",     icon: Briefcase },
  { to: "/companion/notices",  label: "Notices",  icon: Bell },
  { to: "/companion/todos",    label: "To-dos",   icon: ListTodo },
  { to: "/companion/calendar", label: "Calendar", icon: CalendarDays },
];

const CompanionLayout = () => {
  const { user, logout } = useCompanionAuth();
  const loc = useLocation();
  const isAdmin = user?.role === "admin";

  // The "Made with Emergent" platform badge is fixed bottom-right and overlaps
  // the right-most tabs of the Companion bottom nav on mobile. Companion is an
  // internal staff PWA — hide the badge while crew are using the app, restore
  // on unmount so other pages (marketing site, admin) are unaffected.
  useEffect(() => {
    const badge = document.getElementById("emergent-badge");
    if (!badge) return;
    const prev = badge.style.display;
    badge.style.setProperty("display", "none", "important");
    return () => {
      badge.style.display = prev;
    };
  }, []);

  return (
    <div className="min-h-screen bg-crest-surface flex flex-col font-body" data-testid="companion-shell">
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-crest-navy text-white border-b border-white/10">
        <div className="px-4 py-3 flex items-center justify-between max-w-3xl mx-auto w-full">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="h-9 w-9 rounded-md bg-crest-green grid place-items-center text-crest-gold font-display font-bold text-lg shrink-0">C</div>
            <div className="min-w-0">
              <div className="font-display text-sm font-semibold leading-tight truncate">Cresttor Companion</div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-crest-gold truncate">
                {user?.name || user?.email} · {isAdmin ? "Admin" : "Crew"}
              </div>
            </div>
          </div>
          <button onClick={logout} aria-label="Sign out" data-testid="companion-logout" className="text-white/80 hover:text-crest-gold p-2 -mr-2">
            <LogOut className="h-5 w-5" />
          </button>
        </div>

        {isAdmin && (
          <div className="px-4 pb-2 flex items-center gap-1 text-xs max-w-3xl mx-auto w-full overflow-x-auto">
            <NavLink to="/companion/admin/jobs/new" className={({ isActive }) =>
              `px-3 py-1.5 rounded-full whitespace-nowrap ${isActive ? "bg-crest-gold text-crest-navy" : "bg-white/10 text-white/85 hover:bg-white/20"}`
            } data-testid="admin-link-new-job">+ New job</NavLink>
            <NavLink to="/companion/admin/crew" className={({ isActive }) =>
              `px-3 py-1.5 rounded-full whitespace-nowrap ${isActive ? "bg-crest-gold text-crest-navy" : "bg-white/10 text-white/85 hover:bg-white/20"}`
            } data-testid="admin-link-crew">Crew</NavLink>
            <NavLink to="/companion/admin/notices/new" className={({ isActive }) =>
              `px-3 py-1.5 rounded-full whitespace-nowrap ${isActive ? "bg-crest-gold text-crest-navy" : "bg-white/10 text-white/85 hover:bg-white/20"}`
            } data-testid="admin-link-new-notice">+ Notice</NavLink>
          </div>
        )}
      </header>

      <main className="flex-1 max-w-3xl mx-auto w-full pb-24" key={loc.pathname}>
        <Outlet />
      </main>

      {/* Bottom nav */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-border z-40 safe-pb">
        <div className="max-w-3xl mx-auto grid grid-cols-5">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center py-2.5 text-[11px] gap-1 transition-colors ${
                  isActive ? "text-crest-green" : "text-crest-navy/55 hover:text-crest-navy"
                }`
              }
              data-testid={`bottom-nav-${n.label.toLowerCase()}`}
            >
              <n.icon className="h-5 w-5" />
              {n.label}
            </NavLink>
          ))}
        </div>
      </nav>

      <Toaster position="top-center" />
    </div>
  );
};

export default CompanionLayout;
